import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GymfinderOverlayContainer } from './service/gymfinder-overlay-container.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { GymDetailsComponent } from './pages/gym/gym-details/gym-details.component';
import { HtmlEditorComponent } from './components/html-editor/html-editor.component';
import { APP_BASE_HREF, CommonModule, DatePipe } from '@angular/common';
import { NgxPermissionsModule } from 'ngx-permissions';
import { LogLevel, NgxAdmIntModule } from '@egym/ngx-adm-int';
import { environment } from 'src/environments/environment';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GymDetailsJsonDialogComponent } from './pages/gym/gym-details-json-dialog/gym-details-json-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { TextFieldModule } from '@angular/cdk/text-field';
import { GymfinderComponent } from './pages/gymfinder/gymfinder.component';
import { HttpClientModule } from '@angular/common/http';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { GymReportComponent } from './components/gym-report/gym-report.component';
import { FileUploadComponent } from './components/common/file-upload/file-upload.component';
import { OpeningHourComponent } from './components/gym-details/opening-hour/opening-hour.component';
import { OpeningTimeEditorComponent } from './components/gym-details/opening-time-editor/opening-time-editor.component';
import { TimeInputComponent } from './components/gym-details/opening-hour/time-input/time-input.component';
import { AddressInfoComponent } from './components/gym-details/address-info/address-info.component';
import { ValidateAddressDialogComponent } from './pages/gym/validate-address-dialog/validate-address-dialog.component';
import { GymInfoComponent } from './components/gym-info/gym-info.component';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { GymImportComponent } from './pages/gym/gym-import/gym-import.component';
import { ExpressionComponent } from './components/search-filter/filter/expression/expression.component';
import { SearchFilterComponent } from './components/search-filter/search-filter/search-filter.component';
import { AddFilterDialogComponent } from './components/search-filter/filter/add-filter-dialog/add-filter-dialog.component';
import { SearchFiltersComponent } from './pages/search-filters/search-filters.component';
import { FilterComponent } from './components/search-filter/filter/filter.component';
import { BlockComponent } from './components/search-filter/filter/block/block.component';
import { SearchFilterTextComponent } from './components/search-filter/search-filter-text/search-filter-text.component';
import { AddSearchFilterDialogComponent } from './components/search-filter/search-filter/add-search-filter-dialog/add-search-filter-dialog.component';
import { SearchFilterItemComponent } from './components/search-filter/search-filter/search-filter-item/search-filter-item.component';
import { AddItemDialogComponent } from './components/search-filter/search-filter/add-item-dialog/add-item-dialog.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FilterSelectComponent } from './components/search-filter/search-filter/search-filter-item/filter-select/filter-select.component';
import { MatBadgeModule } from '@angular/material/badge';
import { AttributeChangeScheduleComponent } from './components/attribute-change-schedule/attribute-change-schedule.component';
import { AddAttributeChangeScheduleDialogComponent } from './components/attribute-change-schedule/add-attribute-change-schedule-dialog/add-attribute-change-schedule-dialog.component';
import { PromoteMembershipTypeDialogComponent } from './components/attribute-change-schedule/promote-membership-type-dialog/promote-membership-type-dialog.component';
import { MatNativeDateModule } from '@angular/material/core';
import { AttributeChangeScheduleTableComponent } from './components/attribute-change-schedule/attribute-change-schedule-table/attribute-change-schedule-table.component';
import { MatTableModule } from '@angular/material/table';
import { AlertComponent } from './components/alert/alert.component';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { AutoCompletePositionDirective } from './directives/auto-complete-position.directive';
import { DropZoneDirective } from './directives/drop-zone.directive';
import { BasicInfoComponent } from './components/gym-details/basic-info/basic-info.component';
import { ContactInfoComponent } from './components/gym-details/contact-info/contact-info.component';
import { AdditionalInfoComponent } from './components/additional-info/additional-info.component';
import { GymComponent } from './pages/gym/gym.component';
import { GymImageComponent } from './components/gym-image/gym-image.component';
import { MatTabsModule } from '@angular/material/tabs';
import { GymImagePreviewComponent } from './components/gym-image-preview/gym-image-preview.component';
import { MatRadioModule } from '@angular/material/radio';
import { OptionsComponent } from './components/options/options.component';
import { OptionsChipsComponent } from './components/options/chips/chips.component';
import { OptionsCheckboxesComponent } from './components/options/checkboxes/checkboxes.component';
import { CreditsComponent } from './components/options/credits/credits.component';
import { UrlValidatorDirective } from './directives/url-validator.directive';
import { ExceptionalOpeningHourComponent } from './components/gym-details/exceptional-opening-hour/exceptional-opening-hours-day.component';
import { QuillModule } from 'ngx-quill';
import { ExternalBookingInfoComponent } from './components/gym-details/external-booking-info/external-booking-info.component';
import { ChangelogComponent } from './pages/changelog/changelog.component';
import { ProfileCompletenessComponent } from './pages/profile-completeness/profile-completeness.component';
import { MatPaginator } from '@angular/material/paginator';
import { ChangelogDetailsComponent } from './components/changelog-details/changelog-details.component';
import { ChangelogOptionsComponent } from './components/changelog-details/changelog-options/changelog-options.component';
import { ChangelogCellComponent } from './components/changelog-details/changelog-cell/changelog-cell.component';
import { ChangelogOptionsStringsComponent } from './components/changelog-details/changelog-options-strings/changelog-options-strings.component';
import { ChangelogOpeningHoursComponent } from './components/changelog-details/changelog-opening-hours/changelog-opening-hours.component';
import { ChangelogExternalBookingInfoComponent } from './components/changelog-details/changelog-external-booking-info/changelog-external-booking-info.component';
import { ChangelogImagesComponent } from './components/changelog-details/changelog-images/changelog-images.component';
import { ChangelogStudioTypeComponent } from './components/changelog-details/changelog-studio-type/changelog-studio-type.component';
import { ClassBookingConfigComponent } from './components/class-booking-config/class-booking-config.component';
import { GeofenceConfigComponent } from './components/geofence-config/geofence-config.component';
import { AccessConfigComponent } from './components/access-config-component/access-config.component';
import { TransactionalEmailsComponent } from './components/gym-details/transactional-emails/transactional-emails.component';

@NgModule({
  declarations: [
    AddFilterDialogComponent,
    AppComponent,
    GymDetailsComponent,
    HtmlEditorComponent,
    GymDetailsJsonDialogComponent,
    GymfinderComponent,
    GymReportComponent,
    FileUploadComponent,
    OpeningHourComponent,
    OpeningTimeEditorComponent,
    TimeInputComponent,
    ExceptionalOpeningHourComponent,
    AddressInfoComponent,
    ValidateAddressDialogComponent,
    GymInfoComponent,
    OptionsComponent,
    OptionsChipsComponent,
    OptionsCheckboxesComponent,
    CreditsComponent,
    FileUploadComponent,
    GymImportComponent,
    SearchFiltersComponent,
    FilterComponent,
    BlockComponent,
    ExpressionComponent,
    SearchFilterTextComponent,
    SearchFilterComponent,
    AddSearchFilterDialogComponent,
    SearchFilterItemComponent,
    AddItemDialogComponent,
    FilterSelectComponent,
    AttributeChangeScheduleComponent,
    AddAttributeChangeScheduleDialogComponent,
    PromoteMembershipTypeDialogComponent,
    AttributeChangeScheduleTableComponent,
    AlertComponent,
    AutoCompletePositionDirective,
    DropZoneDirective,
    BasicInfoComponent,
    ContactInfoComponent,
    AdditionalInfoComponent,
    GymComponent,
    GymImageComponent,
    GymImagePreviewComponent,
    UrlValidatorDirective,
    ExternalBookingInfoComponent,
    ChangelogComponent,
    ProfileCompletenessComponent,
    ChangelogDetailsComponent,
    ChangelogOptionsComponent,
    ChangelogCellComponent,
    ChangelogOptionsStringsComponent,
    ChangelogOpeningHoursComponent,
    ChangelogExternalBookingInfoComponent,
    ChangelogImagesComponent,
    ChangelogStudioTypeComponent,
    ClassBookingConfigComponent,
    GeofenceConfigComponent,
    AccessConfigComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    DragDropModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    ClipboardModule,
    FormsModule,
    HttpClientModule,
    MatExpansionModule,
    MatBadgeModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatListModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatRadioModule,
    MatSnackBarModule,
    MatTableModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTabsModule,
    MatMenuModule,
    NgxPermissionsModule.forRoot(),
    NgxAdmIntModule.forSpaApplication({
      applicationName: environment.appName,
      production: environment.production,
      logLevel: LogLevel.DEBUG,
    }),
    TextFieldModule,
    MatCheckboxModule,
    MatChipsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatMomentDateModule,
    QuillModule.forRoot(),
    MatPaginator,
    TransactionalEmailsComponent,
  ],
  providers: [
    DatePipe,
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: OverlayContainer, useClass: GymfinderOverlayContainer },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
